<script setup lang="ts">
  import { useCustomToast } from '@/composables/useCustomToast';
  import type { ToastVariant } from '@/composables/useCustomToast';

  const { toasts, dismissToast } = useCustomToast();

  const iconMap: Record<ToastVariant, string> = {
    success: 'lucide:check-circle',
    error: 'lucide:x-circle',
    info: 'lucide:info',
    warning: 'lucide:alert-triangle',
  };
</script>

<template>
  <Teleport to="body">
    <div class="custom-toast-container">
      <TransitionGroup name="toast">
        <div v-for="toast in toasts" :key="toast.id" class="custom-toast" :class="`custom-toast--${toast.type}`">
          <div class="custom-toast__accent" v-if="toast.type !== 'info'" />
          <div class="custom-toast__icon">
            <iconify-icon :icon="iconMap[toast.type]" />
          </div>
          <div class="flex flex-col gap-4">
            <span v-if="toast.title" class="custom-toast__title">{{ toast.title }}</span>
            <span class="custom-toast__message">{{ toast.message }}</span>
          </div>
          <button class="custom-toast__close" @click="dismissToast(toast.id)">
            <iconify-icon icon="lucide:x" />
          </button>
          <div class="custom-toast__progress" :style="{ animationDuration: toast.duration + 'ms' }" />
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
  .custom-toast-container {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 99999;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    pointer-events: none;
  }

  .custom-toast {
    pointer-events: auto;
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 300px;
    max-width: 440px;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.08),
      0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;

    &__accent {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      border-radius: 12px 0 0 12px;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 8px;
      flex-shrink: 0;
      font-size: 16px;
    }

    &__title {
      font-size: 16px;
      font-weight: 500;
      color: #1e293b;
      line-height: 1.4;
    }

    &__message {
      flex: 1;
      font-size: 13.5px;
      font-weight: 400;
      color: #1e293b;
      line-height: 1.4;
    }

    &__close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      color: #94a3b8;
      cursor: pointer;
      border-radius: 6px;
      font-size: 14px;
      flex-shrink: 0;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #475569;
      }
    }

    &__progress {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2.5px;
      width: 100%;
      border-radius: 0 0 12px 12px;
      transform-origin: left;
      animation: toast-progress linear forwards;
    }

    // ----- Type variants -----
    &--success {
      .custom-toast__accent {
        background: linear-gradient(to bottom, #22c55e, #16a34a);
      }
      .custom-toast__icon {
        background: rgba(34, 197, 94, 0.12);
        color: #16a34a;
      }
      .custom-toast__progress {
        background: linear-gradient(to right, #22c55e, transparent);
      }
    }

    &--error {
      .custom-toast__accent {
        background: linear-gradient(to bottom, #ef4444, #dc2626);
      }
      .custom-toast__icon {
        background: rgba(239, 68, 68, 0.12);
        color: #dc2626;
      }
      .custom-toast__progress {
        background: linear-gradient(to right, #ef4444, transparent);
      }
    }

    &--info {
      .custom-toast__accent {
        background: linear-gradient(to bottom, #3b82f6, #2563eb);
      }
      .custom-toast__icon {
        background: rgba(59, 130, 246, 0.12);
        color: #2563eb;
      }
      .custom-toast__progress {
        background: linear-gradient(to right, #3b82f6, transparent);
      }
    }

    &--warning {
      .custom-toast__accent {
        background: linear-gradient(to bottom, #f59e0b, #d97706);
      }
      .custom-toast__icon {
        background: rgba(245, 158, 11, 0.12);
        color: #d97706;
      }
      .custom-toast__progress {
        background: linear-gradient(to right, #f59e0b, transparent);
      }
    }
  }

  // ----- Transition animations -----
  .toast-enter-active {
    animation: toast-in 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
  }

  .toast-leave-active {
    animation: toast-out 0.28s cubic-bezier(0.06, 0.71, 0.55, 1) forwards;
  }

  .toast-move {
    transition: transform 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  }

  @keyframes toast-in {
    0% {
      opacity: 0;
      transform: translateY(-16px) scale(0.96);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes toast-out {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-12px) scale(0.96);
    }
  }

  @keyframes toast-progress {
    from {
      transform: scaleX(1);
    }
    to {
      transform: scaleX(0);
    }
  }
</style>
